import { charityIsTiltify, charityIsTwitch, isYes } from '@util/common.js';
import PropTypes from 'prop-types';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import GroupedColorPicker from '@components/pickers/grouped-color.picker.jsx';
import LinkSetup from '@components/overlays/shared/link-setup.jsx';
import CharitySection from '@components/shared/charity-section.jsx';
import YesNoButtonInput from '@components/input/yes-no-button.input.jsx';

CustomizeFullProgressBar.propTypes = {
  setConfigState: PropTypes.func.isRequired,
  overlayURL: PropTypes.string.isRequired,
  overlayName: PropTypes.string.isRequired,
  obsDimensions: PropTypes.shape({
    width: PropTypes.string,
    height: PropTypes.string,
  }),
  config: PropTypes.shape({
    charityType: PropTypes.string,
    textColor: PropTypes.string,
    bgColor: PropTypes.string,
    showChallenges: PropTypes.oneOf(['yes', 'no']),
    showRewards: PropTypes.oneOf(['yes', 'no']),
    showDonors: PropTypes.oneOf(['yes', 'no']),
    showScroller: PropTypes.oneOf(['yes', 'no']),
  }),
  children: PropTypes.node,
  childrenSlot: PropTypes.oneOf(['top', 'bottom', 'middle', 'scroller']),
};

export default function CustomizeFullProgressBar({
  setConfigState,
  overlayURL,
  config,
  overlayName,
  obsDimensions,
  children,
  childrenSlot = 'top',
}) {
  if (
    !isYes(config.showRewards) &&
    !isYes(config.showChallenges) &&
    !isYes(config.showDonors) &&
    isYes(config.showScroller)
  )
    setConfigState({ target: { name: 'showScroller', value: 'no' } });
  else if (
    (isYes(config.showRewards) ||
      isYes(config.showChallenges) ||
      isYes(config.showDonors)) &&
    !isYes(config.showScroller)
  )
    setConfigState({ target: { name: 'showScroller', value: 'yes' } });

  // Handles switching from Tiltify -> Twitch so that invalid options aren't shown
  if (charityIsTwitch(config.charityType)) {
    if (isYes(config.showRewards))
      setConfigState({ target: { name: 'showRewards', value: 'no' } });
    if (isYes(config.showChallenges))
      setConfigState({ target: { name: 'showChallenges', value: 'no' } });
  }

  return (
    <div className="mt-0 ml-auto mr-auto mb-4 md:px-2 px-0 w-full">
      <CharitySection
        onChange={setConfigState}
        charityType={config.charityType}
      />

      {childrenSlot === 'top' && <div>{children}</div>}

      <CustomizeSection title="Colors">
        <GroupedColorPicker
          textColor={config.textColor}
          bgColor={config.bgColor}
          onChange={setConfigState}
        />
      </CustomizeSection>

      {childrenSlot === 'middle' && <div>{children}</div>}

      <CustomizeSection title="Scroller">
        {childrenSlot === 'scroller' && <div className="mb-4">{children}</div>}

        <div className="flex flex-col md:flex-row">
          <YesNoButtonInput
            className="md:mr-4 mb-2 md:mb-0 flex-1"
            onChange={setConfigState}
            mode="checkbox"
            currentValue={config.showRewards}
            name="showRewards"
            label="Display Rewards"
            labelTextClassName="text-white"
            hidden={!charityIsTiltify(config.charityType)}
          />

          <YesNoButtonInput
            className="md:mr-4 mb-2 md:mb-0 flex-1"
            onChange={setConfigState}
            mode="checkbox"
            currentValue={config.showChallenges}
            name="showChallenges"
            label="Display Targets"
            hidden={!charityIsTiltify(config.charityType)}
          />

          <YesNoButtonInput
            className="flex-1"
            onChange={setConfigState}
            mode="checkbox"
            currentValue={config.showDonors}
            name="showDonors"
            label="Display Donors"
          />
        </div>
      </CustomizeSection>

      {childrenSlot === 'bottom' && <div>{children}</div>}

      <LinkSetup
        charityType={config.charityType}
        obsDimensions={obsDimensions}
        overlayName={overlayName}
        overlayURL={overlayURL}
      />
    </div>
  );
}
